import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { LocalizationService } from '@abp/ng.core';
import { filter, map, take } from 'rxjs/operators';
import { NotificationService } from 'src/core/services/notification/notification.service';

@Injectable({ providedIn: 'root' })
export class BrowserTitleService {
  private readonly titleDictionary = new Map<string, string>([
    ['languages', 'LanguageManagement::Languages'],
    ['texts', 'LanguageManagement::LanguageTexts'],
    ['organization-units', 'AbpIdentity::OrganizationUnits'],
    ['roles', 'AbpIdentity::Roles'],
    ['users', 'AbpIdentity::Users'],
    ['identity/user-groups', 'UserGroup::Menu:UserGroups'],
    ['security-logs', 'AbpIdentity::SecurityLogs'],
    ['list', 'PremiumReporting::Menu:PremiumReportsList'],
    ['view', 'PremiumReporting::Menu:PremiumReportsView'],
    ['tenants', 'Saas::Tenants'],
    ['editions', 'Saas::Editions'],
  ]);

  private currentTitle = '';
  private notificationTitleInterval: any;
  private notificationCount = 0;
  private notificationTitleState = true;
  private idParam: number = null;
  private initialized = false;

  constructor(
    private router: Router,
    private titleService: Title,
    private localizationService: LocalizationService,
    private ngZone: NgZone,
    private notificationService: NotificationService
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.resolveCurrentRouteTitle()),
      )
      .subscribe(data => {
        if (!data) {
          return;
        }

        let title = this.localizationService.instant(data);
        this.currentTitle = title;

        if (this.idParam > 0) {
          this.currentTitle = this.idParam + ' - ' + title;
        }

        this.ngZone.onStable.pipe(take(1)).subscribe(() => {
          this.titleService.setTitle(this.currentTitle);
        });
      });

    this.notificationService.notificationReceived.subscribe(() => {
      if (document.hidden) {
        this.notificationCount += 1;
        this.startNotificationTitleTimer();
      }
    });

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private handleVisibilityChange = () => {
    if (!document.hidden) {
      this.stopNotificationTitleTimer();
    }
  };

  private startNotificationTitleTimer(): void {
    clearInterval(this.notificationTitleInterval);
    this.toggleNotificationTitle();
    this.notificationTitleInterval = setInterval(() => this.toggleNotificationTitle(), 3000);
  }

  private stopNotificationTitleTimer(): void {
    clearInterval(this.notificationTitleInterval);
    this.notificationTitleState = true;
    this.notificationCount = 0;
    this.titleService.setTitle(this.currentTitle);
  }

  private toggleNotificationTitle(): void {
    if (this.notificationTitleState) {
      this.titleService.setTitle(
        this.localizationService.instant('::TitleNotification', this.notificationCount.toString()),
      );
    } else {
      this.titleService.setTitle(this.currentTitle);
    }

    this.notificationTitleState = !this.notificationTitleState;
  }

  private resolveCurrentRouteTitle(): string | null {
    let child = this.router.routerState.snapshot.root.firstChild;
    this.idParam = null;

    while (child) {
      if (child.params?.id > 0) {
        this.idParam = child.params.id;
      }

      if (child.firstChild) {
        child = child.firstChild;
      } else if (child.data && child.data['title']) {
        return child.data['title'];
      } else {
        const routePath = child.routeConfig?.path;

        if (routePath && this.titleDictionary.has(routePath)) {
          return this.titleDictionary.get(routePath);
        }

        return null;
      }
    }

    return null;
  }
}
